import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPatch, apiDelete } from "@/lib/api";
import { ProgressBar } from "@/components/ProgressBar";
import { classAccent } from "@/lib/brand";
import { Building2, ChevronRight, Trash2 } from "lucide-react";
import { Skeleton } from "./shared";

function OrgStudents({ org }) {
  const navigate = useNavigate();
  if (org.students.length === 0) return <p className="text-sm text-muted-foreground">No students in this organization yet.</p>;
  return (
    <div className="space-y-3">
      {org.students.map((s) => (
        <button
          key={s.id}
          onClick={() => navigate(`/admin/students/${s.id}`)}
          data-testid={`overview-student-${s.id}`}
          className="w-full text-left rounded-2xl border-2 border-ink bg-white p-4 press flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <div className="sm:w-48 shrink-0">
            <div className="font-bold">{s.name}</div>
            <div className="text-xs text-muted-foreground">
              Mentor: <b>{s.mentor_name || "—"}</b>
            </div>
          </div>
          <div className="flex-1 grid sm:grid-cols-3 gap-3 w-full">
            {(s.class_progress || []).map((c, i) => (
              <div key={c.id}>
                <div className="flex justify-between text-[11px] font-bold mb-1">
                  <span className="truncate">{c.title}</span>
                  <span>{c.progress}%</span>
                </div>
                <ProgressBar value={c.progress} color={classAccent(c.slug, i, c.color).bg} height={7} />
              </div>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
}

function OrgCard({ org, open, onToggleOpen, onToggleActive, onDelete }) {
  const active = org.is_active ?? true;
  return (
    <div className="rounded-3xl border-2 border-ink bg-white overflow-hidden shadow-pop-sm">
      <div className="w-full flex items-center justify-between px-4 sm:px-6 py-4 gap-3">
        <button onClick={onToggleOpen} data-testid={`org-row-${org.id}`} className="flex items-center gap-3 text-left flex-1 min-w-0 press">
          <div className="h-11 w-11 shrink-0 rounded-2xl border-2 border-ink grid place-items-center" style={{ background: active ? "#B8FF3B" : "#E5E1D5" }}>
            <Building2 size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-extrabold text-lg flex items-center gap-2">
              <span className="truncate">{org.name}</span>
              {!active && <span className="text-[10px] px-2 py-0.5 rounded-full border border-ink bg-[#FFDede] shrink-0">inactive</span>}
            </div>
            <div className="text-xs text-muted-foreground font-semibold">
              {org.students.length} students · {org.mentors.length} mentors · {org.leaders.length} leaders
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onToggleActive} data-testid={`org-toggle-active-${org.id}`} className="h-9 px-3 rounded-xl border-2 border-ink text-xs font-bold press bg-white">
            {active ? "Deactivate" : "Activate"}
          </button>
          {!active && (
            <button onClick={onDelete} data-testid={`delete-org-${org.id}`}
              className="h-9 w-9 grid place-items-center rounded-xl border-2 border-ink press"
              style={{ background: "#FF3B7A", color: "#FFFDF5" }} title="Delete organization">
              <Trash2 size={14} />
            </button>
          )}
          <button onClick={onToggleOpen} className="h-9 w-9 grid place-items-center">
            <ChevronRight size={20} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t-2 border-ink p-4 sm:p-6" style={{ background: "#FFFDF5" }}>
          <OrgStudents org={org} />
        </div>
      )}
    </div>
  );
}

export function OverviewTab() {
  const [data, setData] = useState(null);
  const [openOrg, setOpenOrg] = useState(null);

  const load = useCallback(() => {
    apiGet("/manage/overview").then((d) => {
      setData(d);
      setOpenOrg((cur) => cur || d.organizations?.[0]?.id || null);
    }).catch((e) => toast.error(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleOrg = async (org) => {
    try { await apiPatch(`/manage/organizations/${org.id}/active`, { is_active: !(org.is_active ?? true) }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteOrg = async (org) => {
    if (!window.confirm(`Permanently delete "${org.name}"? It must be empty. This cannot be undone.`)) return;
    try { await apiDelete(`/manage/organizations/${org.id}`); toast.success(`${org.name} deleted`); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (!data) return <Skeleton />;

  return (
    <div className="space-y-4" data-testid="overview-tab">
      {data.organizations.map((org) => (
        <OrgCard key={org.id} org={org} open={openOrg === org.id}
          onToggleOpen={() => setOpenOrg(openOrg === org.id ? null : org.id)}
          onToggleActive={() => toggleOrg(org)} onDelete={() => deleteOrg(org)} />
      ))}
    </div>
  );
}
